import { ref, unref } from 'vue';
import { escapeSql } from '@rdrive/helper-sql';
import { getIconClass } from '@rdrive/helper-icon-class';

// Map a v-data-table column key onto the SQL expression to order by.
const COLUMN_MAPPING = {
  'profileId': 'it.title',
  'locationId': 'l.title',
  'StatusId': 'st.title',
  'statusId': 'st.title',
  'AssigneeTeamId': 't.name',
  'assigneeTeamId': 't.name',
  'AssigneeUserIds': 'json_array_length(fi.assigneeUserIds)',
  'assigneeUserIds': 'json_array_length(fi.assigneeUserIds)',
  'createdByTeamId': 'ct.name',
  'clientId': 'fi.clientId',
  'StatusCreatedDate': 'fi.statusCreatedDate',
  'statusCreatedDate': 'fi.statusCreatedDate',
  'updatedDate': 'fi.statusCreatedDate'
};

// Text columns get case-insensitive ordering; numeric/date columns must keep their native sort
const CASE_INSENSITIVE_COLUMNS = new Set(['it.title', 'l.title', 'st.title', 't.name', 'ct.name']);

const JOINS_CLAUSE = `
            LEFT JOIN item_templates it ON fi.profileId = it.id
            LEFT JOIN categories c ON it.categoryId = c.id
            LEFT JOIN form_templates ft ON fi.SubItemTemplateId = ft.id
            LEFT JOIN status_templates st ON fi.StatusId = st.id
            LEFT JOIN teams t ON fi.AssigneeTeamId = t.id
            LEFT JOIN locations l ON fi.locationId = l.id
            LEFT JOIN drawings ld ON l.drawingId = ld.id
            LEFT JOIN teams ct ON fi.createdByTeamId = ct.id
        `;

/**
 * All the DuckDB work behind ItemsGrid: the user lookup, and one page of
 * grouped item rows.
 *
 * `fetchPage` is DuckDbDataTable's provider (through ItemsGrid's `fetchPage` prop): it loads
 * the user lookup first (guarded, idempotent) and never throws — a failure logs and answers
 * with no rows and 0 items.
 *
 * @param {object} deps
 * @param {object|import('vue').Ref} deps.conn       DuckDB connection
 * @param {string|import('vue').Ref} deps.itemsView  source view, e.g. 'filteredItems'
 *   or the Activity page's 'filteredItemsByStatusHistory'
 * @returns {{
 *   users: import('vue').Ref<Object>,
 *   fetchPage: (opts: { page: number, itemsPerPage: number, sort: object, search: string })
 *     => Promise<{ items: Array, totalItems: number }>
 * }}
 */
export function useItemsGridData({ conn, itemsView }) {
  // Local users cache loaded from DuckDB
  const users = ref({});

  const db = () => unref(conn);
  const view = () => unref(itemsView);

  const loadUsers = async () => {
    if (Object.keys(users.value).length > 0 && !users.value['undefined']) return; // Already loaded
    try {
      // Load users from key-value structured table
      // Schema: Key (team ID) | Value (JSON array of users)
      const result = await db().query('SELECT Key, Value FROM users');
      const byId = {};

      result.toArray().forEach(row => {
        const rowData = row.toJSON();
        let teamUsers = rowData.Value || rowData.value;

        // Parse the JSON string to get array of users
        if (typeof teamUsers === 'string') {
          try {
            teamUsers = JSON.parse(teamUsers);
          } catch {
            teamUsers = [];
          }
        }

        // Extract users from this team
        if (Array.isArray(teamUsers)) {
          teamUsers.forEach(u => {
            const id = u.Id || u.id;
            if (id) {
              // Only add if not already added (avoid duplicates across teams)
              if (!byId[id]) {
                byId[id] = {
                  displayName: u.DisplayName || u.displayName || '',
                  avatarUrl: u.AvatarUrl || u.avatarUrl || ''
                };
              }
            }
          });
        }
      });

      users.value = byId;
    } catch (error) {
      console.error('Error loading users from DB:', error);
    }
  };

  // NOTE the alias here is `fi.` — useItemsGridReport's copy uses `i.`. The two
  // queries select from different join sets, so they are deliberately kept as
  // separate functions rather than parameterised on the alias (R5).
  const getWhereClause = (search) => {
    let whereClause = '';
    if (search && search.trim()) {
      const searchTerm = escapeSql(search.trim().toLowerCase());
      whereClause = `
                WHERE (
                    LOWER(fi.clientId) LIKE '%${searchTerm}%'
                    OR LOWER(it.title) LIKE '%${searchTerm}%'
                    OR LOWER(t.name) LIKE '%${searchTerm}%'
                    OR LOWER(l.title) LIKE '%${searchTerm}%'
                    OR LOWER(ld.title) LIKE '%${searchTerm}%'
                    OR LOWER(st.title) LIKE '%${searchTerm}%'
                    OR LOWER(ct.name) LIKE '%${searchTerm}%'
                    OR strftime(TRY_CAST(fi.statusCreatedDate AS TIMESTAMP), '%Y-%m-%d') LIKE '%${searchTerm}%'
                )
            `;
    }
    return whereClause;
  };

  const buildOrderBy = (sort) => {
    // Default sort column (capital I for DuckDB case sensitivity)
    let orderByColumn = 'fi.Id';
    const orderDirection = sort.order || 'desc';

    // Track if we need JOINs for sorting
    let needsJoinsForSort = false;

    if (sort.key) {
      if (sort.key.startsWith('subitem_')) {
        orderByColumn = sort.key;
      } else {
        orderByColumn = COLUMN_MAPPING[sort.key] || `fi.${sort.key.toLowerCase()}`;

        // Check if the sort column requires JOINs (references other tables)
        needsJoinsForSort = orderByColumn.startsWith('it.') ||
                           orderByColumn.startsWith('l.') ||
                           orderByColumn.startsWith('st.') ||
                           orderByColumn.startsWith('t.') ||
                           orderByColumn.startsWith('ct.');
      }
    }

    const orderByExpression = CASE_INSENSITIVE_COLUMNS.has(orderByColumn) ? `lower(${orderByColumn})` : orderByColumn;
    let orderByClause = `ORDER BY ${orderByExpression} ${orderDirection.toUpperCase()}`;

    // clientId values like "Issue-100" sort by prefix first, then numeric suffix as an integer,
    // so "Issue-2" comes before "Issue-10" (mirrors compareClientIds on main)
    if (orderByColumn === 'fi.clientId') {
      const direction = orderDirection.toUpperCase();
      const nullsPosition = direction === 'ASC' ? 'NULLS FIRST' : 'NULLS LAST';
      orderByClause = `ORDER BY ` +
        `lower(CASE WHEN regexp_matches(fi.clientId, '-[0-9]+$') THEN regexp_replace(fi.clientId, '-[0-9]+$', '') ELSE fi.clientId END) ${direction}, ` +
        `TRY_CAST(nullif(regexp_extract(fi.clientId, '-([0-9]+)$', 1), '') AS BIGINT) ${direction} ${nullsPosition}`;
    }

    return { orderByClause, needsJoinsForSort };
  };

  // Rows arrive one-per-sub-item; fold them into parent items carrying a
  // subItems array, then derive the "latest sub-item" summary fields.
  const groupRows = (rawRows) => {
    const groupedItems = {};

    rawRows.forEach(rawData => {
      const parentId = rawData.id || rawData.Id; // Handle both lowercase and uppercase Id

      if (!groupedItems[parentId]) {
        // Create parent item the first time we see this parent ID
        groupedItems[parentId] = {
          id: rawData.id || rawData.Id,
          clientId: rawData.clientId || rawData.ClientId,
          profileId: rawData.profileId || rawData.ProfileId,
          type: rawData.type || rawData.Type,
          profileTitle: rawData.profileTitle,
          categoryId: rawData.categoryId,
          subcategoryId: rawData.subcategoryId,
          categoryIcon: getIconClass(rawData.categoryIcon),
          formTemplateTitle: rawData.formTemplateTitle || '',
          locationId: rawData.locationId || rawData.LocationId,
          locationTitle: rawData.locationTitle || rawData.LocationTitle,
          drawingId: rawData.drawingId || rawData.DrawingId,
          drawingTitle: rawData.drawingTitle || rawData.DrawingTitle,
          createdByTeamName: rawData.createdByTeamName,
          createdByUserName: users.value[rawData.createdByUserId || rawData.CreatedByUserId]?.displayName || '',
          // Initialize the subitems array
          subItems: []
        };
      }

      // Parse assigneeUserIds from the raw data (stored as JSON string in DB)
      let assigneeUserIds = [];
      const rawAssigneeUserIds = rawData.assigneeUserIds || rawData.AssigneeUserIds;
      if (rawAssigneeUserIds) {
        if (typeof rawAssigneeUserIds === 'string') {
          try {
            const parsed = JSON.parse(rawAssigneeUserIds);
            assigneeUserIds = Array.isArray(parsed) ? parsed : [parsed].filter(Boolean);
          } catch {
            // If not valid JSON, try comma-separated
            assigneeUserIds = rawAssigneeUserIds.split(',').map(id => id.trim()).filter(Boolean);
          }
        } else if (Array.isArray(rawAssigneeUserIds)) {
          assigneeUserIds = rawAssigneeUserIds;
        }
      }

      // Convert assigneeUserIds to user objects
      const assigneeUsers = assigneeUserIds
        .filter(id => users.value && users.value[id])
        .map(id => ({
          id: id,
          displayName: users.value[id]?.displayName || '',
          avatarUrl: users.value[id]?.avatarUrl || ''
        }));

      // Add this row as a subitem
      // Handle both PascalCase and camelCase column names from DuckDB
      const subitem = {
        formTemplateTitle: rawData.formTemplateTitle,
        formTemplateSort: rawData.formTemplateSort ?? Number.MAX_SAFE_INTEGER,
        statusId: rawData.statusId || rawData.StatusId,
        statusTitle: rawData.statusTitle,
        statusColor: rawData.statusColor,
        statusCreatedDate: rawData.statusCreatedDate || rawData.StatusCreatedDate,
        assigneeTeamName: rawData.assigneeTeamName || '',
        assigneeUserIds: assigneeUserIds,
        assigneeUsers: assigneeUsers,
        locationTitle: rawData.locationTitle || '',
        drawingId: rawData.drawingId || '',
        drawingTitle: rawData.drawingTitle || ''
      };

      // Add the subitem to the parent's subitems array
      groupedItems[parentId].subItems.push(subitem);
    });

    let items = Object.values(groupedItems);

    // Sort subItems: primary = statusCreatedDate desc, secondary = formTemplateSort asc
    items.forEach(item => {
      if (item.subItems && item.subItems.length > 1) {
        item.subItems.sort((a, b) => {
          const aDate = a.statusCreatedDate ? new Date(a.statusCreatedDate).getTime() : 0;
          const bDate = b.statusCreatedDate ? new Date(b.statusCreatedDate).getTime() : 0;
          if (aDate !== bDate) return bDate - aDate;
          const aSort = Number(a.formTemplateSort ?? Number.MAX_SAFE_INTEGER);
          const bSort = Number(b.formTemplateSort ?? Number.MAX_SAFE_INTEGER);
          return aSort - bSort;
        });
      }
    });

    // Add the new properties to each item by finding the latest statusCreatedDate
    return items.map(item => {
      // Find the subitem with the latest statusCreatedDate
      let latestSubitem = null;

      if (item.subItems && item.subItems.length > 0) {
        latestSubitem = item.subItems.reduce((latest, current) => {
          // If statusCreatedDate is missing, use a very old date
          const latestDate = latest.statusCreatedDate ? new Date(latest.statusCreatedDate) : new Date(0);
          const currentDate = current.statusCreatedDate ? new Date(current.statusCreatedDate) : new Date(0);

          return currentDate > latestDate ? current : latest;
        }, item.subItems[0]);
      }

      // Add the new properties
      return {
        ...item,
        latestStatusTitle: latestSubitem ? latestSubitem.statusTitle : '',
        latestStatusColor: latestSubitem ? latestSubitem.statusColor : '',
        updatedDate: latestSubitem ? latestSubitem.statusCreatedDate : null
      };
    });
  };

  const fetchPage = async ({ page, itemsPerPage, sort, search }) => {
    try {
      // Row mapping needs the user lookup; the call is guarded and idempotent
      await loadUsers();

      const whereClause = getWhereClause(search);
      const { orderByClause, needsJoinsForSort } = buildOrderBy(sort);

      // JOINs needed when searching OR when sort column requires them
      const needsJoins = whereClause.length > 0 || needsJoinsForSort;

      // Build the LIMIT and OFFSET clauses
      const limitClause = `LIMIT ${itemsPerPage}`;
      const offsetClause = `OFFSET ${(page - 1) * itemsPerPage}`;

      // Count total items
      const countQuery = `
            SELECT COUNT(DISTINCT fi.id) as total
            FROM ${view()} fi
            ${needsJoins ? JOINS_CLAUSE : ''}
            ${whereClause}
        `;

      const countResult = await db().query(countQuery);
      let myTotalItems = 0;
      if (countResult.numRows > 0) {
        myTotalItems = Number(countResult.toArray()[0].toJSON().total);
      }

      //since we have subItems first calculate for the pageSize how many items to return
      const itemsIdsQuery = `
            SELECT DISTINCT
                fi.id
            FROM ${view()} fi
            ${needsJoins ? JOINS_CLAUSE : ''}
            ${whereClause}
            ${orderByClause}
            ${limitClause}
            ${offsetClause}
        `;

      const idsResult = await db().query(itemsIdsQuery);
      const itemIds = idsResult.toArray().map(row => row.toJSON().id);

      if (itemIds.length === 0) {
        return {
          items: [],
          totalItems: myTotalItems
        };
      }

      // Get the main items with all necessary joined data
      const itemsQuery = `
            SELECT
                fi.*,
                it.title             AS profileTitle,
                it.categoryId,
                it.subcategoryId,
                c.icon               AS categoryIcon,
                ft.title             AS formTemplateTitle,
                ft.sort              AS formTemplateSort,
                st.title             AS statusTitle,
                st.color             AS statusColor,
                t.name               AS assigneeTeamName,
                l.title              AS locationTitle,
                l.drawingId          AS drawingId,
                ld.title             AS drawingTitle,
                ct.name              AS createdByTeamName
            FROM ${view()} fi
            LEFT JOIN item_templates it ON fi.profileId       = it.id
            LEFT JOIN categories     c  ON it.categoryId      = c.id
            LEFT JOIN form_templates ft ON fi.subItemTemplateId = ft.id
            LEFT JOIN status_templates st ON fi.statusId      = st.id
            LEFT JOIN teams          t  ON fi.assigneeTeamId  = t.id
            LEFT JOIN locations      l  ON fi.locationId      = l.id
            LEFT JOIN drawings       ld ON l.drawingId        = ld.id
            LEFT JOIN teams          ct ON fi.createdByTeamId = ct.id
            WHERE fi.id IN (${itemIds.map(id => `'${escapeSql(id)}'`).join(',')})
            ${orderByClause};
        `;

      const itemsResult = await db().query(itemsQuery);

      return {
        items: groupRows(itemsResult.toArray().map(row => row.toJSON())),
        totalItems: myTotalItems
      };
    } catch (error) {
      console.error('Error fetching data from DuckDB:', error);
      return {
        items: [],
        totalItems: 0
      };
    }
  };

  return { users, fetchPage };
}
